import EventEmitter from 'client/javascript/util/event_emitter';
import { get } from 'shared/util/helpers';
import { varsFromServer } from 'client/javascript/client_side_injection_vars';
import { performAlgoliaNearbyBuildingsSearch } from 'client/javascript/geo';
import { getLocationId } from 'client/javascript/get_closest_market';
import { localizeLink } from 'client/javascript/util/locale';
import { get as fetchURL } from 'client/http';

export const SOLUTION_DETAILS_EVENT_NAMES = {
  ID_UPDATE: {
    USER_CLOSEST_MARKET: 'closestMarketIdUpdate',
    USER_150_MILES_MARKET: '150MilesMarketIdUpdate'
  },
  DATA_UPDATE: {
    USER_CLOSEST_MARKET: 'closestMarketSolutionsDataUpdate',
    USER_150_MILES_MARKET: '150MilesMarketSolutionsDataUpdate'
  }
};

const pageUrlSearchParams = new URLSearchParams(window.location.search);

export default class SolutionDetailsData extends EventEmitter {
  constructor() {
    super();

    const { currentSolution = {}, compareSolutions = [] } = varsFromServer;

    this.minCapacity = 1;
    this.buildingsLimit = 8;
    this.closestMarketId = null;
    this.closestMarketIn150MilesId = null;
    this.solutionsDataForClosestMarket = null;
    this.solutionsDataFor150MilesMarket = null;
    this.currentSolution = currentSolution;
    this.compareSolutions = compareSolutions;
    this.shouldDisableContentfulCache = pageUrlSearchParams.get('we_disable_cf_cache') || false;

    // initial setup
    this.updateClosestMarketIds()
      .then(this.updatePricingForMarketIn150Miles.bind(this))
      .then(this.updatePricingForClosestMarket.bind(this));
  }

  async updateClosestMarketIds() {
    // TODO: optimize pricing fetch (see: https://jira.weworkers.io/browse/MPWW-12943)
    const [buildings150MilesAlgoliaRes, closestMarketId] = await Promise.all([
      performAlgoliaNearbyBuildingsSearch(),
      getLocationId()
    ]);

    this.closestMarketIn150MilesId = get(buildings150MilesAlgoliaRes, 'results[0].hits[0].marketgeo.id', null);
    this.closestMarketId = closestMarketId || null;

    this.emit(SOLUTION_DETAILS_EVENT_NAMES.ID_UPDATE.USER_CLOSEST_MARKET, this.closestMarketId);
    this.emit(SOLUTION_DETAILS_EVENT_NAMES.ID_UPDATE.USER_150_MILES_MARKET, this.closestMarketIn150MilesId);
  }

  async updatePricingForMarketIn150Miles() {
    const { closestMarketIn150MilesId, minCapacity, buildingsLimit, shouldDisableContentfulCache } = this;

    if (closestMarketIn150MilesId == null) {
      return this.emit(SOLUTION_DETAILS_EVENT_NAMES.DATA_UPDATE.USER_150_MILES_MARKET, null);
    }

    const queryParams = {
      minCapacity,
      buildingsLimit,
      we_disable_cf_cache: shouldDisableContentfulCache
    };

    const productDataFor150MilesMarketURL = localizeLink({
      to: `/products/pricing/${encodeURIComponent(closestMarketIn150MilesId)}`
    });

    const productData = await fetchURL(productDataFor150MilesMarketURL, { params: queryParams });

    this.solutionsDataFor150MilesMarket = productData.data;
    this.emit(SOLUTION_DETAILS_EVENT_NAMES.DATA_UPDATE.USER_150_MILES_MARKET, this.solutionsDataFor150MilesMarket);
  }

  async updatePricingForClosestMarket() {
    const { closestMarketId, minCapacity, buildingsLimit, shouldDisableContentfulCache } = this;

    if (closestMarketId == null) {
      return this.emit(SOLUTION_DETAILS_EVENT_NAMES.DATA_UPDATE.USER_CLOSEST_MARKET, null);
    }

    const queryParams = {
      minCapacity,
      buildingsLimit,
      we_disable_cf_cache: shouldDisableContentfulCache
    };

    const productDataForClosestMarketURL = localizeLink({
      to: `/products/pricing/${encodeURIComponent(closestMarketId)}`
    });

    const productData = await fetchURL(productDataForClosestMarketURL, { params: queryParams });

    this.solutionsDataForClosestMarket = productData.data;
    this.emit(SOLUTION_DETAILS_EVENT_NAMES.DATA_UPDATE.USER_CLOSEST_MARKET, this.solutionsDataForClosestMarket);
  }

  updateMinCapacity(n) {
    this.minCapacity = n;
    this.updatePricingForClosestMarket();
  }

  updateClosestMarketId(id) {
    this.closestMarketId = id;
  }
}
